import { Link } from "react-router-dom";
import { Menu, X, User, LogOut, Settings } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import logoTupja from "@/assets/logo-tupja.png";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
// SpotifyPlayer removed - now embedded on homepage
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserProfile {
  full_name: string;
  avatar_url: string | null;
}

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      if (session?.user) {
        loadProfile(session.user.id);
        checkAdminRole(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        loadProfile(session.user.id);
        checkAdminRole(session.user.id);
      } else {
        setProfile(null);
        setIsAdmin(false);
      }
    });

    // Listen for avatar updates from Profile page
    const handleAvatarUpdate = (e: CustomEvent<{ avatarUrl: string }>) => {
      setProfile(prev => prev ? { ...prev, avatar_url: e.detail.avatarUrl } : null);
    };

    window.addEventListener('avatar-updated', handleAvatarUpdate as EventListener);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('avatar-updated', handleAvatarUpdate as EventListener);
    };
  }, []);

  const loadProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("full_name, avatar_url")
      .eq("id", userId)
      .single();
    if (data) setProfile(data);
  };

  const checkAdminRole = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!data);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/sobre-umbanda", label: t("nav.aboutUmbanda") },
    { to: "/quem-somos", label: t("nav.aboutUs") },
    { to: "/trabalhos-espirituais", label: t("nav.spiritualWorks") },
    { to: "/normas", label: t("nav.houseRules") },
    { to: "/doacoes", label: t("nav.donations") },
    { to: "/galeria", label: t("nav.gallery") },
    { to: "/biblioteca", label: t("nav.library") },
    { to: "/contato", label: t("nav.contact") },
  ];

  return (
    <nav className="bg-card/90 backdrop-blur-md border-b-2 border-primary/20 sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between min-h-32 py-2">
          <Link to="/" className="flex items-center group pt-1">
            <img src={logoTupja} alt="TUPJA Logo" className="w-24 h-24 object-contain group-hover:scale-105 transition-transform duration-300" />
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className="px-2.5 py-1.5 text-foreground/90 hover:text-logo-brown hover:bg-muted/50 transition-all duration-200 text-sm font-sans font-medium rounded-md whitespace-nowrap">
                {link.label}
              </Link>
            ))}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="ml-2">
                  <Avatar className="w-10 h-10 border-2 border-primary/30 hover:border-primary transition-colors cursor-pointer">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10"><User className="w-5 h-5 text-primary" /></AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/perfil" className="flex items-center gap-2"><User className="w-4 h-4" />{t('auth.myProfile')}</Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2"><Settings className="w-4 h-4" />{t('nav.administration')}</Link>
                    </DropdownMenuItem>
                  )}
                  {!isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/agendamento-seu-ze" className="flex items-center gap-2">{t('nav.scheduleAppointment')}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-destructive">
                    <LogOut className="w-4 h-4" />{t('auth.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="px-2.5 py-1.5 bg-logo-brown text-white hover:bg-logo-brown/90 transition-all duration-200 text-base font-sans font-medium rounded-md shadow-md whitespace-nowrap ml-2">
                {t('nav.bookAppointment')}
              </Link>
            )}
            
            <div className="ml-4 flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-primary hover:text-secondary transition-colors p-2" aria-label="Toggle menu">
            {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden pb-6 animate-fade-in border-t border-border/50 mt-2 pt-4">
            <div className="flex justify-center items-center gap-4 mb-4">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)} className="block py-3 px-4 text-foreground/90 hover:text-logo-brown hover:bg-muted/50 transition-all duration-200 rounded-md mb-1 font-sans text-xl">
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/perfil" onClick={() => setIsOpen(false)} className="block py-3 px-4 text-foreground/90 hover:text-logo-brown hover:bg-muted/50 transition-all duration-200 rounded-md mb-1 font-sans text-xl">
                  {t('auth.myProfile')}
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="block py-3 px-4 text-foreground/90 hover:text-logo-brown hover:bg-muted/50 transition-all duration-200 rounded-md mb-1 font-sans text-xl">
                    {t('nav.administration')}
                  </Link>
                )}
                {!isAdmin && (
                  <Link to="/agendamento-seu-ze" onClick={() => setIsOpen(false)} className="block py-3 px-4 text-foreground/90 hover:text-logo-brown hover:bg-muted/50 transition-all duration-200 rounded-md mb-1 font-sans text-xl">
                    {t('nav.scheduleAppointment')}
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="block w-full text-left py-3 px-4 text-destructive hover:bg-muted/50 transition-all duration-200 rounded-md mb-1 font-sans text-xl">
                  {t('auth.logout')}
                </button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setIsOpen(false)} className="block py-3 px-4 bg-logo-brown text-white hover:bg-logo-brown/90 transition-all duration-200 rounded-md mb-1 font-sans text-xl text-center shadow-md">
                {t('nav.bookAppointment')}
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};